import CampaignRepository from "../domain/data/CampaignRepository";
import SigningDocument from "../domain/entities/_SigningDocument";
import Campaign, { TargetAudience } from "../domain/entities/Campaign";
import User from "../domain/entities/User";
import AwsApiClient from "../presentation/hooks/AwsApiClient";

export default class APICampaignRepository implements CampaignRepository {
  private client = new AwsApiClient();

  async deleteCampaign(
    user: User,
    campaign: Campaign,
    targetAudience: TargetAudience
  ): Promise<boolean> {
    try {
      const response = await this.client.delete(
        `/api/campaign/${targetAudience}/${user.organization.id}/${user.id}/${campaign.id}`,
        "none"
      );
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveCampaign(user: User, campaign: Campaign): Promise<Campaign> {
    try {
      const response = await this.client.postExternal(
        `/api/campaign`,
        campaign
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaign(user: User, campaignId: string): Promise<Campaign> {
    try {
      const response = await this.client.get(
        `/api/campaign/${user.organization.id}/${user.id}/${campaignId}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaignExt(shortId: string): Promise<Campaign> {
    try {
      const response = await this.client.getExternal(
        `/api/ext/campaign/${shortId}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaigns(
    user: User,
    targetAudience: TargetAudience
  ): Promise<Campaign[]> {
    try {
      const response = await this.client.get(
        `/api/campaigns/${targetAudience}/${user.organization.id}/${user.id}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaignsbyLookup(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string
  ): Promise<Campaign[]> {
    try {
      const response = await this.client.get(
        `/api/campaigns/${targetAudience}/${user.organization.id}/${user.id}/${lookupType}/${lookupValue}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getAllCampaigns(user: User): Promise<Campaign[]> {
    try {
      const response = await this.client.get(
        `/api/campaigns/${user.organization.id}/${user.id}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaignsPagination(
    user: User,
    targetAudience: string,
    pageSize: number,
    lastEvaluatedKey: Record<string, string> | null
  ): Promise<{ items: Campaign[]; lastEvaluatedKey?: any }> {
    try {
      const requestBody = {
        pageSize,
        lastEvaluatedKey,
      };
  
      const response = await this.client.post(
        `/api/campaigns/pagination/${targetAudience}/${user.organization.id}/${user.id}`,
        requestBody
      );
  
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaignsByLookupPagination(
    user: User,
    targetAudience: string,
    lookupType: string,
    lookupValue: string,
    pageSize: number,
    lastEvaluatedKey: Record<string, string> | null,
    existingCampaignIds: string[] | null
  ): Promise<{ items: Campaign[]; lastEvaluatedKey?: any }> {
    try {
      const requestBody = {
        lookupType,
        lookupValue,
        pageSize,
        lastEvaluatedKey,
        existingCampaignIds,
      };
  
      const response = await this.client.post(
        `/api/campaigns/lookup/pagination/${targetAudience}/${user.organization.id}/${user.id}`,
        requestBody
      );
  
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }
  
  async getCampaignsCount(
    user: User,
    targetAudience: string
  ): Promise<number> {
    try {
      const response = await this.client.get(
        `/api/campaigns/count/${targetAudience}/${user.organization.id}/${user.id}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaignsLookupsCount(
    user: User,
    targetAudience: string,
    lookupType: string,
    lookupValue: string
  ): Promise<number> {
    try {
      const requestBody = { lookupType, lookupValue };

      const response = await this.client.post(
        `/api/campaigns/lookup/count/${targetAudience}/${user.organization.id}/${user.id}`,
        requestBody
      );
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async sendCampaign(
    user: User,
    campaign: Campaign
  ): Promise<string | undefined> {
    let endpointUrl = `/api/campaign/activate/${user.organization.id}/${user.id}/${campaign.id}`;
    if (campaign.status.toUpperCase() === "STARTED") {
      endpointUrl = `/api/campaign/resend/${user.organization.id}/${user.id}/${campaign.id}`;
    }
    /*if (signatoryId) {
      endpointUrl = `/api/signatory/request/${user.id}/${campaign.id}/${signatoryId}`;
    }*/
    try {
      const response = await this.client.post(endpointUrl, null, "none");
      return campaign.id;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      console.log("Error: ", message);
      throw new Error(message);
    }
  }

  async saveSendCampaign(
    user: User,
    campaign: Campaign
  ): Promise<boolean> {
    try {
      const response = await this.client.post(
        `/api/campaign/sendStaff`, campaign
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      console.log("Error: ", message);
      throw new Error(message);
    }
  }

  async getSigningDocument(
    user: User,
    campaign: Campaign
  ): Promise<SigningDocument[]> {
    try {
      const url = `/api/documents/${user.organization.id}/${user.id}/${campaign.id}`;
      console.log(`📡 API Request: GET ${url}`);
      
      const response = await this.client.get(url);
      
      console.log(`📡 API Response:`, {
        url,
        campaignName: campaign.name,
        documentsCount: Array.isArray(response) ? response.length : 'not an array',
        firstDoc: Array.isArray(response) && response.length > 0 ? {
          id: response[0].id,
          status: response[0].status,
          signatory: response[0].signatory?.firstName + ' ' + response[0].signatory?.lastName
        } : 'none'
      });
      
      return response;
    } catch (error) {
      console.error(`📡 API Error for campaign ${campaign.name}:`, error);
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getCampaignEmailTracking(
    user: User,
    campaign: Campaign
  ): Promise<{ tracking: any; stats: any }> {
    try {
      const response = await this.client.get(
        `/api/email-tracking/campaign/${user.organization.id}/${campaign.id}`
      );
      console.log('📧 API Response - Email Tracking:', {
        url: `/api/email-tracking/campaign/${user.organization.id}/${campaign.id}`,
        response,
        trackingKeys: response.tracking ? Object.keys(response.tracking) : [],
        statsKeys: response.stats ? Object.keys(response.stats) : []
      });
      return response;
    } catch (error) {
      const message = `An error has occured fetching email tracking: ${error}`;
      console.error(message);
      // Return empty tracking data instead of throwing - tracking is optional
      return { tracking: {}, stats: {} };
    }
  }
}
